/**
 * Flow-direction boundary transforms.
 *
 * The solver always works in "flow space", where the dependency flow runs
 * left→right (+x): every directional term — flow bias, SGD ordering
 * constraints, port/wall anchoring, the topo-rank seed — is written against
 * +x once. A requested screen direction is applied purely at the boundary:
 * inputs (previous, pinned, node sizes) map INTO flow space, every emitted
 * result maps back OUT. LR/TB are exact (pure swap); the RL/BT mirrors use
 * `-(x+w)`, whose addition can round by ~1ulp on fractional coordinates —
 * pinned rects therefore bypass the round-trip entirely (packResult emits
 * the raw input pin), and sub-ulp drift on free nodes is irrelevant.
 *
 *   LR — identity (default).
 *   RL — mirror x.
 *   TB — swap axes (flow runs top→bottom on screen).
 *   BT — swap axes + mirror (flow runs bottom→top).
 */

import type { Rect } from "./stressLayout.js";

export type FlowDirection = "LR" | "RL" | "TB" | "BT";

/** Screen-space rect → flow-space rect. */
export function toFlowRect(r: Rect, dir: FlowDirection): Rect {
  switch (dir) {
    case "LR":
      return r;
    case "RL":
      return { x: -(r.x + r.w), y: r.y, w: r.w, h: r.h };
    case "TB":
      return { x: r.y, y: r.x, w: r.h, h: r.w };
    case "BT":
      return { x: -(r.y + r.h), y: r.x, w: r.h, h: r.w };
  }
}

/** Flow-space rect → screen-space rect. Exact inverse of `toFlowRect`. */
export function fromFlowRect(r: Rect, dir: FlowDirection): Rect {
  switch (dir) {
    case "LR":
      return r;
    case "RL":
      return { x: -(r.x + r.w), y: r.y, w: r.w, h: r.h };
    case "TB":
      return { x: r.y, y: r.x, w: r.h, h: r.w };
    case "BT":
      return { x: r.y, y: -(r.x + r.w), w: r.h, h: r.w };
  }
}

/** True when the screen flow axis is vertical (node w/h swap in flow space). */
export function isVertical(dir: FlowDirection): boolean {
  return dir === "TB" || dir === "BT";
}
